import React, { useMemo, useState } from 'react';
import {
  ChipFilterOption,
  FilterComponentProps,
} from '../../FilterBottomSheet.types';
import { AllieChip, AllieChipColorProps, AllieInput } from '../../../../../components';
import { AllieColors } from '../../../../../common/color.types';
import { OptionsList } from '../../FilterBottomSheet.styles';
import { AllieSearchOutline } from '../../../../../styling/icons';

const colors: {
  active: AllieChipColorProps;
  outline: AllieChipColorProps;
} = {
  active: {
    backgroundColor: 'primary-80' as AllieColors,
    allieColor: 'white' as AllieColors,
    borderColor: 'primary-80' as AllieColors,
  },
  outline: {
    backgroundColor: 'neutral-5' as AllieColors,
    allieColor: 'primary-90' as AllieColors,
    borderColor: 'neutral-30' as AllieColors,
  },
};

export const ChipFilter: React.FC<FilterComponentProps<ChipFilterOption>> = ({
  options,
  selectedValues,
  onChange,
  maxLength,
  multiple = false,
  showSearch = false,
  required = false,
}) => {
  const [searchText, setSearchText] = useState('');

  const handleChipClick = (value: string, isSelected: boolean) => {
    setSearchText('');
    if (multiple) {
      const currentValues = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      onChange(newValues);
    } else if (!(required && isSelected)) {
      onChange(value === selectedValues ? '' : value);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const normalizeText = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const { selectedOptions, filteredOptions } = useMemo(() => {
    const selected = options.filter((item) =>
      multiple
        ? Array.isArray(selectedValues) && selectedValues.includes(item.value)
        : selectedValues === item.value
    );

    const filtered = options.filter((item) =>
      normalizeText(item.text).includes(normalizeText(searchText))
    );

    return { selectedOptions: selected, filteredOptions: filtered };
  }, [options, selectedValues, searchText, multiple]);

  const displayedItems = useMemo(() => {
    if (maxLength === undefined) {
      return searchText ? filteredOptions : options;
    }
    return [...selectedOptions, ...filteredOptions.slice(0, maxLength)];
  }, [selectedOptions, filteredOptions, maxLength, searchText, options]);

  const showSearchComponent = showSearch || (maxLength && options.length > maxLength);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {showSearchComponent && (
        <AllieInput
          corner="rounded-edges"
          allieSize="small"
          onChange={handleInputChange}
          value={searchText}
          suffix={
            <AllieSearchOutline
              style={{ fontSize: '20px', color: 'var(--allie-color-neutral-50)' }}
              slot="icon-only"
            />
          }
        />
      )}
      <OptionsList>
        {displayedItems.map((option, index) => {
          const isSelected = selectedOptions.includes(option);
          const chipColor = isSelected ? colors.active : colors.outline;

          return (
            <AllieChip
              key={`${option.value}-${index}`}
              color={chipColor}
              radius="full"
              onClick={() => handleChipClick(option.value, isSelected)}
            >
              {option.text}
            </AllieChip>
          );
        })}
      </OptionsList>
    </div>
  );
};
